/**
 * The in-game settings sheet — Blockari's `SettingsPanel` in its `.dialog`,
 * in the kit's markup. Reached from the gear during a round (which pauses)
 * and from the results screen (which does not).
 *
 * Quality · Sound with its three sliders · Notifications and Haptics, then
 * Done. A paused round shows its score and clock above the settings and Done
 * reads Resume; Quit / Forfeit sit under it while a game runs.
 */

import { el, formatScore, prefersReducedMotion } from '@/ui/hud';
import { enablePush, disablePush, pushState, type PushState } from '@/net/notify';
import type { AudioMixer } from '@/ui/screens';

export interface PauseScreenCallbacks {
  onResume(): void;
  onRestart(): void;
  /** Leave the round for the title screen. */
  onQuit?: () => void;
  /** Hand the round in as it stands. Offered on a ranked round only. */
  onForfeit?: () => void;
}

export interface PauseScreenOptions {
  /**
   * Mixer to build the sound controls against. Omit and the sheet has none —
   * which is what the tests that predate them do.
   */
  audio?: AudioMixer;
  /** The quality tier: read, and pinned. `null` is auto. */
  quality?: {
    get(): QualityChoice;
    set(choice: QualityChoice): void;
  };
}

/** The four rungs of the quality seg: the governor's own choice, or a pin. */
export type QualityChoice = 'auto' | 'high' | 'medium' | 'low';
export const QUALITY_CHOICES: readonly QualityChoice[] = ['auto', 'high', 'medium', 'low'];

const TIER_HINT: Record<QualityChoice, string> = {
  auto: 'Picked for this device, and stepped down if the frame rate drops.',
  high: 'Everything on: bloom, aberration, grain. Desktop GPUs.',
  medium: 'Bloom and grade; no aberration. Most phones.',
  low: 'The plain table, no post-processing.',
};

const PUSH_HINT: Record<PushState, string> = {
  off: 'Get told when someone takes your challenge and when a match settles.',
  on: 'On for this device.',
  denied: 'Blocked in the browser\u2019s site settings for 21 Wild.',
  'needs-install': 'Add 21 Wild to your Home Screen first.',
  unsupported: 'Not available on this device.',
};

const HAPTICS_KEY = 'wild21.haptics';

/** Whether taps are wanted. On by default on a device that is held, as Blockari has it; off once turned off. */
export function hapticsOn(): boolean {
  try {
    const stored = localStorage.getItem(HAPTICS_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    // Private mode: fall through to the default.
  }
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

/** Where the settings live between sessions, for whoever needs them. */
export function setHaptics(on: boolean): void {
  try {
    localStorage.setItem(HAPTICS_KEY, on ? '1' : '0');
  } catch {
    // Private mode: the switch works for this session and forgets.
  }
}

/** The kit's `Switch`: a checkbox, its track, and a label with a hint. */
export function kitSwitch(
  name: string,
  hint: string,
  checked: boolean,
  onChange: (on: boolean) => void,
): { root: HTMLLabelElement; input: HTMLInputElement; hint: HTMLElement } {
  const root = el('label', 'w21-switch') as HTMLLabelElement;
  const input = el('input') as HTMLInputElement;
  input.type = 'checkbox';
  input.checked = checked;
  input.addEventListener('change', () => onChange(input.checked));
  const track = el('span', 'w21-switch-track');
  track.setAttribute('aria-hidden', 'true');
  const label = el('span', 'w21-switch-label');
  const small = el('small', undefined, hint);
  label.append(el('span', undefined, name), small);
  root.append(input, track, label);
  return { root, input, hint: small };
}

/**
 * The settings controls — quality, sound with its three sliders, notifications, haptics — as one block, so
 * the in-game sheet and the Settings page (Blockari's `/settings`) are the same controls.
 */
export class SettingsControls {
  readonly root = el('div', 'w21-settings');

  readonly #seg = el('div', 'w21-seg');
  readonly #tierHint = el('div', 'w21-tier-hint');
  readonly #sound: ReturnType<typeof kitSwitch> | null = null;
  readonly #ranges: Array<{ input: HTMLInputElement; value: HTMLElement; kind: 'master' | 'music' | 'sfx' }> = [];
  readonly #notify = el('div', 'w21-notify-row');
  readonly #notifySwitch: ReturnType<typeof kitSwitch>;
  readonly #haptics: HTMLElement | null = null;

  #push: PushState = 'unsupported';

  constructor(private readonly options: PauseScreenOptions = {}) {
    const settings = this.root;
    /* -- quality ---------------------------------------------------------------- */
    const quality = el('div', 'w21-group');
    const head = el('div', 'w21-group-head');
    head.append(el('span', 'w21-field-label', 'Quality'));
    this.#seg.setAttribute('role', 'radiogroup');
    this.#seg.setAttribute('aria-label', 'Quality tier');
    quality.append(head, this.#seg, this.#tierHint);

    /* -- sound ------------------------------------------------------------------ */
    const sound = el('div', 'w21-group');
    const mixer = options.audio;
    if (mixer) {
      this.#sound = kitSwitch('Sound', 'Music leads; effects sit under it.', !mixer.muted, (on) => {
        mixer.setMuted(!on);
        this.sync();
      });
      const sliders = el('div', 'w21-sliders');
      for (const [label, kind] of [
        ['Master', 'master'],
        ['Music', 'music'],
        ['Effects', 'sfx'],
      ] as const) {
        const row = el('label', 'w21-slider');
        const input = el('input', 'w21-range') as HTMLInputElement;
        input.type = 'range';
        input.min = '0';
        input.max = '100';
        input.setAttribute('aria-label', `${label} volume`);
        const value = el('b');
        input.addEventListener('input', () => {
          const level = Number(input.value) / 100;
          if (kind === 'master') mixer.setVolume(level);
          else if (kind === 'music') mixer.setMusicVolume(level);
          else mixer.setSfxVolume(level);
          // A slider pushed above zero is a clear statement that you want to
          // hear this again, so it un-mutes.
          if (level > 0) {
            if (kind === 'master' && mixer.muted) mixer.setMuted(false);
            if (kind === 'music' && mixer.musicMuted) mixer.setMusicMuted(false);
            if (kind === 'sfx' && mixer.sfxMuted) mixer.setSfxMuted(false);
          }
          this.sync();
        });
        row.append(el('span', undefined, label), input, value);
        sliders.append(row);
        this.#ranges.push({ input, value, kind });
      }
      sound.append(this.#sound.root, sliders);
    }

    /* -- notifications and haptics ---------------------------------------------- */
    const rows = el('div', 'w21-group');
    this.#notifySwitch = kitSwitch('Notifications', PUSH_HINT.off, false, (on) => void this.#flipPush(on));
    this.#notify.append(this.#notifySwitch.root);
    rows.append(this.#notify);
    // Rendered only where the device can buzz at all; locked unless it is
    // the kind of device that is held.
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
      const haptics = el('div', coarse ? 'w21-haptics-row' : 'w21-haptics-row w21-locked');
      const HAPTICS_ON = 'A tap on a placement, a clear, a bust and a rank-up.';
      const sw = kitSwitch(
        'Haptics',
        coarse ? (hapticsOn() ? HAPTICS_ON : 'Off') : 'Not available on this device.',
        coarse && hapticsOn(),
        (on) => {
          setHaptics(on);
          sw.hint.textContent = on ? HAPTICS_ON : 'Off';
        },
      );
      sw.input.disabled = !coarse;
      haptics.append(sw.root);
      rows.append(haptics);
      this.#haptics = haptics;
    }
    settings.append(quality, sound, rows);
    this.paintQuality();
    this.sync();
  }

  /** Re-read the mixer into the switch and the sliders. */
  sync(): void {
    const mixer = this.options.audio;
    if (!mixer || !this.#sound) return;
    const on = !mixer.muted;
    this.#sound.input.checked = on;
    this.#sound.hint.textContent = on ? 'Music leads; effects sit under it.' : 'Muted';
    for (const range of this.#ranges) {
      const level =
        range.kind === 'master'
          ? mixer.volume
          : range.kind === 'music'
            ? mixer.musicVolume
            : mixer.sfxVolume;
      const muted =
        range.kind === 'master' ? mixer.muted : range.kind === 'music' ? mixer.musicMuted : mixer.sfxMuted;
      // The value stays while muted — 80 greyed, not 0 — so turning sound
      // back on returns to where it was. Only a channel's own mute zeroes it.
      const pct = Math.round((muted && range.kind !== 'master' ? 0 : level) * 100);
      range.input.value = String(pct);
      range.input.style.setProperty('--v', `${pct}%`);
      range.value.textContent = String(pct);
      range.input.disabled = !on;
    }
  }

  /** Ask the browser where push stands and draw it. */
  async syncPush(): Promise<void> {
    if (this.#forced) return;
    const state = await pushState();
    if (this.#forced) return;
    this.#push = state;
    this.#paintPush();
  }

  /** Pin the push state — the frame harness only; the browser's answer never arrives here. */
  forcePush(state: PushState): void {
    this.#push = state;
    this.#forced = true;
    this.#paintPush();
  }

  #forced = false;

  /** The notifications row, for a caller that wants to refresh it. */
  get notifications(): { sync: () => Promise<void> } {
    return { sync: () => this.syncPush() };
  }

  #paintPush(): void {
    const on = this.#push === 'on';
    const live = this.#push === 'on' || this.#push === 'off';
    this.#notify.classList.toggle('w21-locked', !live);
    this.#notifySwitch.input.checked = on;
    this.#notifySwitch.input.disabled = !live;
    this.#notifySwitch.hint.textContent = PUSH_HINT[this.#push];
  }

  async #flipPush(on: boolean): Promise<void> {
    this.#push = on ? await enablePush() : await disablePush();
    this.#paintPush();
  }

  paintQuality(): void {
    const current = this.options.quality?.get() ?? 'auto';
    const index = Math.max(0, QUALITY_CHOICES.indexOf(current));
    this.#seg.style.setProperty('--n', String(QUALITY_CHOICES.length));
    this.#seg.style.setProperty('--i', String(index));
    const thumb = el('span', 'w21-seg-thumb');
    thumb.setAttribute('aria-hidden', 'true');
    this.#seg.replaceChildren(
      thumb,
      ...QUALITY_CHOICES.map((choice) => {
        const on = choice === current;
        const item = el('button', 'w21-seg-item', choice) as HTMLButtonElement;
        item.type = 'button';
        item.setAttribute('role', 'radio');
        item.setAttribute('aria-checked', String(on));
        item.tabIndex = on ? 0 : -1;
        item.addEventListener('click', () => {
          this.options.quality?.set(choice);
          this.paintQuality();
        });
        // A radiogroup: the arrow keys move the choice and the focus.
        item.addEventListener('keydown', (event) => {
          const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
          if (!step) return;
          event.preventDefault();
          const next = QUALITY_CHOICES[(QUALITY_CHOICES.indexOf(choice) + step + QUALITY_CHOICES.length) % QUALITY_CHOICES.length];
          if (!next) return;
          this.options.quality?.set(next);
          this.paintQuality();
          (this.#seg.querySelector('[aria-checked="true"]') as HTMLElement | null)?.focus();
        });
        return item;
      }),
    );
    this.#tierHint.textContent = TIER_HINT[current];
  }
}

export class PauseScreen {
  readonly root: HTMLElement;
  readonly resumeButton: HTMLButtonElement;
  readonly restartButton: HTMLButtonElement;

  readonly #dialog = el('div', 'w21-dialog');
  readonly #title = el('h2', undefined, 'Settings');
  readonly #strip = el('div', 'w21-pause-strip');
  readonly #stripScore = el('b', undefined, '0');
  readonly #stripLeft = el('b', undefined, '0:00');
  readonly #stripMode = el('span', 'w21-chip', 'Solo');
  readonly #stripDivider = el('div', 'w21-divider w21-labelled', 'Settings');
  readonly #lede = el('p', 'w21-lede', 'The clock keeps running.');
  readonly #forfeit: HTMLButtonElement;
  readonly #controls: SettingsControls;

  constructor(
    private readonly callbacks: PauseScreenCallbacks,
    private readonly options: PauseScreenOptions = {},
  ) {
    // The sheet is Blockari's `.overlay > .dialog` inside the game root: the
    // page under it never scrolls, the dialog itself does.
    this.root = el('div', 'w21-screen w21-screen--pause w21-overlay');
    this.root.dataset.active = 'false';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', 'Settings');

    this.#dialog.setAttribute('role', 'dialog');
    this.#dialog.setAttribute('aria-labelledby', 'w21-settings-title');
    this.#title.id = 'w21-settings-title';

    /* -- the paused round's strip -------------------------------------------- */
    const score = el('div', 'w21-stat');
    score.append(el('span', 'w21-t-label', 'Score'), this.#stripScore);
    const left = el('div', 'w21-stat');
    left.append(el('span', 'w21-t-label', 'Left'), this.#stripLeft);
    this.#strip.append(score, left, this.#stripMode);
    this.#strip.hidden = true;
    this.#stripDivider.hidden = true;

    this.#controls = new SettingsControls(options);
    const settings = this.#controls.root;


    /* -- actions ---------------------------------------------------------------- */
    const actions = el('div', 'w21-actions');
    this.resumeButton = el('button', 'w21-btn w21-primary w21-block w21-lg') as HTMLButtonElement;
    this.resumeButton.type = 'button';
    this.resumeButton.textContent = 'Done';
    this.resumeButton.addEventListener('click', () => callbacks.onResume());
    this.restartButton = el('button', 'w21-btn w21-block w21-ghost') as HTMLButtonElement;
    this.restartButton.type = 'button';
    this.restartButton.textContent = 'Quit game';
    this.restartButton.addEventListener('click', () => (callbacks.onQuit ?? callbacks.onRestart)());
    this.#forfeit = el('button', 'w21-btn w21-block w21-danger') as HTMLButtonElement;
    this.#forfeit.type = 'button';
    this.#forfeit.textContent = 'Forfeit — submit current score';
    this.#forfeit.addEventListener('click', () => callbacks.onForfeit?.());
    actions.append(this.resumeButton, this.restartButton, this.#forfeit);

    this.#lede.hidden = true;
    this.#dialog.append(this.#title, this.#lede, this.#strip, this.#stripDivider, settings, actions);
    this.root.append(this.#dialog);
    this.sync();
  }

  /**
   * Open the sheet.
   *
   * With a `paused` round the head is the round — its score, its clock, the
   * mode — and Done reads Resume; without one it is plain Settings, as from
   * the results screen.
   */
  show(
    round: { score: number; secondsLeft: number; mode: string; ranked: boolean; running?: boolean } | null = null,
  ): void {
    this.root.hidden = false;
    this.root.dataset.active = 'true';
    // A solo round pauses under the sheet: "Paused", the strip, Resume. A
    // ranked one keeps running: "Settings", a lede saying so, Back to the
    // game. From results: plain Settings and Done.
    const running = round?.running === true;
    this.#title.textContent = round && !running ? 'Paused' : 'Settings';
    this.#lede.hidden = !running;
    this.#strip.hidden = round === null || running;
    this.#stripDivider.hidden = round === null || running;
    if (round) {
      this.#stripScore.textContent = formatScore(round.score);
      const m = Math.floor(round.secondsLeft / 60);
      const s = Math.max(0, Math.round(round.secondsLeft % 60));
      this.#stripLeft.textContent = `${m}:${String(s).padStart(2, '0')}`;
      this.#stripMode.textContent = round.mode;
    }
    this.resumeButton.textContent = round === null ? 'Done' : running ? 'Back to the game' : 'Resume';
    // Quit on a solo round, Forfeit on a ranked one; neither from results.
    this.restartButton.hidden = round === null || round.ranked;
    this.#forfeit.hidden = round === null || !round.ranked;
    // The mixer can have been changed by a keyboard shortcut since this sheet
    // was last seen, so re-read rather than trusting what was drawn.
    this.sync();
    this.#controls.paintQuality();
    void this.syncPush();
    if (!prefersReducedMotion()) {
      requestAnimationFrame(() => {
        if (this.root.isConnected) this.resumeButton.focus({ preventScroll: true });
      });
    }
  }

  hide(): void {
    this.root.dataset.active = 'false';
    this.root.hidden = true;
  }

  get isOpen(): boolean {
    return this.root.dataset.active === 'true';
  }

  destroy(): void {
    this.root.remove();
  }

  /** Re-read the mixer into the switch and the sliders. */
  sync(): void {
    this.#controls.sync();
  }

  /** Ask the browser where push stands and draw it. */
  syncPush(): Promise<void> {
    return this.#controls.syncPush();
  }

  /** Pin the push state — the frame harness only; the browser's answer never arrives here. */
  forcePush(state: PushState): void {
    this.#controls.forcePush(state);
  }

  /** The notifications row, for a caller that wants to refresh it. */
  get notifications(): { sync: () => Promise<void> } {
    return this.#controls.notifications;
  }
}
